/**
 * The application shell — masthead, module strip, footer.
 *
 * Ported from the editorial-v2 masthead idiom (`draft-3-editorial-v2.html`) in the compact
 * proportions the inner pages use (`reporting-page-v1.html`, `official-profile-detail-v1.html`):
 * emblem slot · breadcrumb + H1 + kicker · clock and the TD chip,
 * over a full-width module nav with the sliding accent underline.
 *
 * **Deliberate deviations from the mockup, all for the same reason — no fabricated data.**
 *  · The AG2026 emblem is a base64 PNG in the mockup, © AINAGOC and licensed for internal
 *    tournament operations. It is not copied into this repository — and since run 32 it does
 *    not have to be: the slot draws whatever `GET /brand/emblem` serves, which is the file an
 *    operator or the Technical Delegate put in the store through Tournament Configuration §01.
 *    With no emblem installed the box is exactly the empty box it has always been, at exactly
 *    the same dimensions, so a masthead does not reflow when the artwork arrives or is taken
 *    away. See {@link MastheadEmblem}.
 *  · The identity chip once showed a fictional TD. It now renders the SIGNED-IN caller from
 *    the session — see {@link IdentityChip} for what it draws and what it refuses to invent.
 *  · The matchday line is not modelled yet and is therefore absent rather than invented.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EVENT'S NAME COMES FROM THE TOURNAMENT RECORD, NOT FROM THIS FILE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The emblem caption, the clock's host-city line and the footer carried
 * `20th Asian Games · Aichi–Nagoya 2026` as fixed text, straight out of the mockup. That was
 * true of one installation and false of every other — including the demonstration database,
 * whose tournament is a different, fictional one, so the masthead contradicted the register
 * beneath it on the same screen.
 *
 * All three now read {@link useTournament}, which is the `tournament` row as `GET /auth/me`
 * reports it (`api/types.ts`, `TournamentBrandingView`).
 *
 * **What is drawn while it is unknown, and it is the same thing in both cases.** `null` means
 * either "the session is still booting" or "this installation has no tournament set up", and
 * neither permits an event name to be printed — so nothing is. No placeholder, no skeleton
 * shimmer in the middle of a masthead, and above all no default event: the caption keeps its
 * box, the clock says `Local time` without claiming a city, and the footer names the
 * application without naming an event. A masthead that flashed a wrong name for one frame
 * would be the original defect at a shorter duration.
 *
 * **The clock, and why the flag went.** The clock now ticks in the TOURNAMENT's zone when one
 * is known, because the line beneath it names the host city and a browser clock beside a host
 * city is two different times presented as one. The mockup's inline Japanese flag was removed
 * rather than made conditional: a flag is a per-host-country asset and this repository holds
 * exactly one drawing, of Japan, so on any other tournament it was decoration that asserted
 * something false. The host city is text, and text follows the record.
 */

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { useBrandAsset } from "../features/branding/useBrandAsset.js";
import { RefreshBanner } from "./RefreshBanner.js";
import { MODULES, moduleForPath } from "./modules.js";
import { PageKickerProvider, usePageKickerValue } from "./pageKicker.js";
import { useSession, useTournament } from "./session.js";

/**
 * `14:09:17`, in the tournament's own zone when one is known and in the device's otherwise.
 *
 * `hour12: false` and `en-GB` with numeric parts only: what comes back is digits and colons,
 * so no ICU spelling can reach the screen. An unknown zone falls back to the device's rather
 * than to a guess at the tournament's, and the caption beside it stops naming a city — see
 * the module note.
 */
function useClock(timezone: string | null): string {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);
  const parts = { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false } as const;
  try {
    return new Intl.DateTimeFormat("en-GB", {
      ...(timezone === null ? {} : { timeZone: timezone }),
      ...parts,
    }).format(now);
  } catch {
    // A `timezone` the runtime does not recognise throws a `RangeError` out of the
    // constructor. A stale IANA name in one column must not take the whole masthead — and
    // every screen under it — down, so the device's own zone stands in.
    return new Intl.DateTimeFormat("en-GB", parts).format(now);
  }
}

/**
 * Sign-out, behind a confirmation — and the confirmation is not politeness.
 *
 * **Nobody can sign themselves back in.** There is no password (spec 09 §1) and no
 * self-service re-send: this application never sends email (D19/D20), so a new link has to
 * come from the Technical Delegate, by hand, out of band. A stray press on a phone in a
 * dugout would therefore cost a person their access until somebody found them — which is
 * exactly the cost the second press exists to prevent. The wording says so plainly rather
 * than asking "are you sure?", which tells nobody anything.
 *
 * No unsynced-queue interstitial yet (spec 09 §E5): the offline note queue is not built, so
 * a dialogue naming a count would be naming a number that does not exist. It belongs here
 * beside this one when spec 10's queue lands, and is flagged for that slice.
 */
function SignOutControl(): React.JSX.Element {
  const { signOut } = useSession();
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return (
      <button
        type="button"
        className="gbtn signout__trigger"
        onClick={() => {
          setArmed(true);
        }}
      >
        Sign Out
      </button>
    );
  }

  return (
    <div className="signout__confirm" role="alertdialog" aria-label="Confirm sign out">
      <p className="signout__warn">
        Signing out clears this device&rsquo;s access. You cannot sign yourself back in —
        only the Technical Delegate can issue a new link.
      </p>
      <div className="signout__acts">
        <button
          type="button"
          className="gbtn"
          onClick={() => {
            setArmed(false);
          }}
        >
          Stay Signed In
        </button>
        <button type="button" className="gbtn gbtn--solid" onClick={signOut}>
          Sign Out Anyway
        </button>
      </div>
    </div>
  );
}

/**
 * The initials for the avatar, from the app short name: `Annie` → `N`, `Hyun Young` → `EJ`.
 *
 * Up to two parts, split on spaces and hyphens, because that is what these names actually
 * are (`Hyun Young`, `Jaswant`). Nothing is derived from a surname, because the identity carries
 * none — `app_short_name` is a first name by design and is *never* a country code (spec 00
 * §2), which is also why no country appears beside it below.
 */
export function chipInitials(appShortName: string): string {
  const parts = appShortName.split(/[\s-]+/).filter((part) => part !== "");
  return parts
    .slice(0, 2)
    .map((part) => (part[0] ?? "").toUpperCase())
    .join("");
}

/** The two kinds of user, and no others (D-3) — as the masthead names them. */
export function chipRoleLabel(kind: "td" | "um"): string {
  return kind === "td" ? "Technical Delegate" : "Umpires Manager";
}

/**
 * WHO IS SIGNED IN, on the masthead — **read from the session and from nothing else**.
 *
 * This chip was a hardcoded fictional Technical Delegate until the UM app gave a second kind
 * of user a reason to be on this origin. A constant was defensible while nothing else was
 * known; it is not defensible now, because a UM who follows a link to a Control Centre path
 * would be shown somebody else's name and, worse, somebody else's ROLE — on a masthead whose
 * whole job is to say whose surface this is.
 *
 * **What is drawn is exactly what `GET /auth/me` carries**: the app short name and the kind.
 *
 * **What is NOT drawn, and is not invented to fill the mockup's shape:**
 *  · the `SURNAME Forename` media form — the identity has a short name, not a roster name;
 *  · the country code beside it (`NZL` in the mockup) — the identity carries no country, and
 *    a short name is never a country code (spec 00 §2). **TODO:** if the masthead is to show
 *    the caller's own country, it needs a field on `GET /auth/me`; that is an API decision for
 *    a slice that owns `apps/api`, not something to fabricate here.
 *
 * `identity` is non-null in practice — `RequireSession` renders this shell only once a session
 * is signed in — but the null branch is real rather than a cast: it says `Signed In` and
 * claims no role at all, which is the honest thing to render when the caller is unknown.
 */
function IdentityChip(): React.JSX.Element {
  const { session } = useSession();
  const identity = session.status === "signed-in" ? session.identity : null;

  if (identity === null) {
    return (
      <div className="tdchip" data-testid="identity-chip">
        <span className="tdchip__avatar" aria-hidden="true">
          ·
        </span>
        <span>
          <span className="tdchip__name">Signed In</span>
        </span>
      </div>
    );
  }

  return (
    <div className="tdchip" data-testid="identity-chip">
      <span className="tdchip__avatar" aria-hidden="true" data-testid="identity-avatar">
        {chipInitials(identity.appShortName)}
      </span>
      <span>
        <span className="tdchip__name">{identity.appShortName}</span>
        <br />
        <span className="tdchip__role">{chipRoleLabel(identity.kind)}</span>
      </span>
    </div>
  );
}

/**
 * The masthead's emblem slot — `.emblem__box`, with the tournament's emblem in it when one has
 * been installed.
 *
 * **The box is the same box in both states.** `.emblem__box` keeps its 62×75 frame, its border
 * and its padding whether or not there is a picture inside it, and the image is fitted into that
 * frame with `object-fit: contain` (RUN 32 rules, appended to the stylesheet). So the masthead
 * does not reflow when the artwork arrives, when it is replaced with one of a different shape,
 * or when the Technical Delegate removes it — which matters more here than anywhere else on the
 * page, because everything to the right of this box is laid out against it.
 *
 * **While it is loading, and when the fetch fails, it draws the empty box** — never a broken
 * image and never a shimmer in the middle of a masthead. `useBrandAsset` resolves a failure as
 * "there is no emblem", which is the honest answer: an emblem that could not be read is not a
 * fault the reader can do anything about from here.
 *
 * `alt` is empty and the box stays `aria-hidden` when there is no picture. Spec 01 §3 gives the
 * emblem a `title` naming the event and pointing at Tournament Configuration; the title here is
 * the tournament's own name from the session, never a fixed one, for the reason the whole module
 * header gives.
 */
function MastheadEmblem(): React.JSX.Element {
  const { asset } = useBrandAsset("emblem");
  const tournament = useTournament();

  if (asset === null) {
    return <div className="emblem__box" aria-hidden="true" data-testid="emblem-box" />;
  }

  const title =
    tournament === null
      ? "Competition emblem — configurable in Tournament Configuration"
      : `${tournament.shortDisplayName} emblem — configurable in Tournament Configuration`;

  return (
    <div className="emblem__box" data-testid="emblem-box" title={title}>
      {/* Decorative: the event is named in the caption immediately beside this box and in the
          masthead heading, so an alt text here would be the third reading of the same fact. */}
      <img className="emblem__img" src={asset.url} alt="" data-testid="emblem-image" />
    </div>
  );
}

/**
 * The Shell proper, inside the kicker provider.
 *
 * The provider has to sit **above** this component rather than around the `<Outlet />`
 * inside it, because it is this component that reads the claim a routed page makes.
 */
function ShellFrame(): React.JSX.Element {
  const location = useLocation();
  const current = moduleForPath(location.pathname);
  const kicker = usePageKickerValue(location.pathname, current.kicker);
  const tournament = useTournament();
  const clock = useClock(tournament?.timezone ?? null);

  return (
    <div className="shell">
      {/* Above the masthead and above everything else, because it is about the page itself
          rather than about anything on it. It draws nothing at all until a newer release is
          actually deployed — see `RefreshBanner.tsx` and the TD's finding of 2026-09-09. */}
      <RefreshBanner />
      <header className="masthead">
        <div className="emblem">
          <MastheadEmblem />
          {/* The box keeps its dimensions whether or not there is a caption to put under it,
              so the masthead does not reflow when the session settles. */}
          <span className="emblem__cap" data-testid="event-caption">
            {tournament === null ? null : (
              <>
                <b>{tournament.shortDisplayName}</b>
                {tournament.hostCity}
              </>
            )}
          </span>
        </div>

        <div className="masthead__brand">
          <nav className="crumb" aria-label="Breadcrumb">
            <Link to="/">Command Desk</Link>
            {current.path === "/" ? null : (
              <>
                <span className="sep" aria-hidden="true">
                  ›
                </span>
                <span className="here" aria-current="page">
                  {current.title}
                </span>
              </>
            )}
          </nav>
          <h1>
            TD Control Centre<span className="thin"> — {current.title}</span>
          </h1>
          <span className="kicker kicker--accent">{kicker}</span>
        </div>

        <div className="masthead__meta">
          {/* The `Tournament Report` link left the masthead on Alfred's ruling of 2026-09-03:
              the FIH form is filled in exactly once, after the closing match, so a button on
              every screen served no working day. The one remaining copy is on the Reporting
              page (`MarkingPage`), where end-of-tournament paperwork already lives. */}
          <div className="clockblock">
            <div className="clock num">
              <span aria-live="off">{clock}</span>
            </div>
            <div className="clock-sub" data-testid="clock-caption">
              {tournament === null ? "Local time" : `Local time · ${tournament.hostCity}`}
            </div>
          </div>
          <IdentityChip />
          <SignOutControl />
        </div>
      </header>

      <nav className="modnav" aria-label="Modules">
        {MODULES.map((entry) => (
          <NavLink
            key={entry.idx}
            to={entry.path}
            end={entry.path === "/"}
            {...(entry.built ? {} : { className: "modnav--soon", title: "Not Built Yet" })}
          >
            <span className="idx">{entry.idx}</span>
            {entry.label}
          </NavLink>
        ))}
      </nav>

      <main>
        <Outlet />
      </main>

      <footer className="foot">
        {/* The competition numbers went with the event name. `1916` and `1917` are the TMS
            competition ids of ONE tournament, and printing them beside a name read from the
            record would have been half a fixed string surviving the fix. They belong to the
            fixture and appointment surfaces, which read them from the competitions of the
            tournament they are showing. */}
        <span data-testid="foot-event">
          {tournament === null
            ? "TD Control Centre"
            : `TD Control Centre · ${tournament.fullNameForLetterheads}`}
        </span>
        <span className="num">Appointments · Reporting · Hearings · Configuration</span>
        {/* The deployed release's timestamp, compiled in by `infra/deploy.sh` (see
            `release.d.ts`) — so a fault report during testing can name the exact build it
            was seen on. A dev server prints `dev`, which is the truth about a bundle no
            release directory holds. */}
        <span className="num" data-testid="foot-release" title="Deployed release">
          Release {__RELEASE__}
        </span>
      </footer>
    </div>
  );
}

export function Shell(): React.JSX.Element {
  return (
    <PageKickerProvider>
      <ShellFrame />
    </PageKickerProvider>
  );
}
